// src/contexts/WarehouseContext.tsx
// Context for managing selected warehouse across the application

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { warehouseService, Warehouse } from '../services/warehouseService';

interface WarehouseContextType {
  selectedWarehouse: Warehouse | null;
  warehouses: Warehouse[];
  loading: boolean;
  error: string | null;
  setSelectedWarehouse: (warehouse: Warehouse | null) => void;
  refreshWarehouses: () => Promise<Warehouse[]>;
  isWarehouseSelected: boolean;
}

const WarehouseContext = createContext<WarehouseContextType | undefined>(undefined);

interface WarehouseProviderProps {
  children: ReactNode;
}

export const WarehouseProvider: React.FC<WarehouseProviderProps> = ({ children }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshWarehouses = async (): Promise<Warehouse[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const warehouseData = await warehouseService.getWarehouses({
        is_active: true,
        for_header: true,
      });
      
      // Ensure warehouseData is always an array
      const validWarehouseData = Array.isArray(warehouseData) ? warehouseData : [];
      setWarehouses(validWarehouseData);

      // Nota: No forzar selección por defecto aquí para permitir "Todos los almacenes" (selectedWarehouse = null)
      return validWarehouseData;
    } catch (err) {
      console.error('Error loading warehouses:', err);
      setError(err instanceof Error ? err.message : 'Error loading warehouses');
      // Ensure warehouses remains an empty array on error
      setWarehouses([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSetSelectedWarehouse = (warehouse: Warehouse | null) => {
    setSelectedWarehouse(warehouse);
    
    // Store in localStorage for persistence
    if (warehouse) {
      localStorage.setItem('selected_warehouse', JSON.stringify(warehouse));
    } else {
      localStorage.removeItem('selected_warehouse');
    }
  };

  // Load persisted warehouse selection and warehouses on mount
  useEffect(() => {
    const initializeWarehouse = async () => {
      try {
        // Check if user is authenticated before making API calls
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) {
          setLoading(false);
          return;
        }

        // Try to restore selected warehouse from localStorage
        const storedWarehouse = localStorage.getItem('selected_warehouse');
        if (storedWarehouse) {
          try {
            const parsed = JSON.parse(storedWarehouse);
            setSelectedWarehouse(parsed);
          } catch (parseError) {
            console.warn('Error parsing stored warehouse:', parseError);
            localStorage.removeItem('selected_warehouse');
          }
        }

        // Load available warehouses
        const loadedWarehouses = await refreshWarehouses();

        // Si no hay selección previa y se oculta "Todos", seleccionar por defecto
        if (!storedWarehouse && loadedWarehouses.length > 0) {
          const defaultWarehouse = loadedWarehouses.find(w => w.is_default) || loadedWarehouses[0];
          if (defaultWarehouse) {
            handleSetSelectedWarehouse(defaultWarehouse);
          }
        }
      } catch (err) {
        console.error('Error initializing warehouse context:', err);
        setError(err instanceof Error ? err.message : 'Error initializing warehouses');
        setLoading(false);
      }
    };

    initializeWarehouse();
  }, []);

  // Verify that selected warehouse is still valid when warehouses change
  useEffect(() => {
    if (selectedWarehouse && warehouses.length > 0) {
      const isValid = warehouses.some(w => w.id === selectedWarehouse.id && w.is_active);
      
      if (!isValid) {
        // Selected warehouse is no longer valid, select default or first available
        const defaultWarehouse = warehouses.find(w => w.is_default) || warehouses[0];
        handleSetSelectedWarehouse(defaultWarehouse || null);
      }
    }
  }, [warehouses, selectedWarehouse]);

  const value: WarehouseContextType = {
    selectedWarehouse,
    warehouses,
    loading,
    error,
    setSelectedWarehouse: handleSetSelectedWarehouse,
    refreshWarehouses,
    isWarehouseSelected: selectedWarehouse !== null,
  };

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};

// Custom hook to use warehouse context
export const useWarehouse = (): WarehouseContextType => {
  const context = useContext(WarehouseContext);
  if (context === undefined) {
    throw new Error('useWarehouse must be used within a WarehouseProvider');
  }
  return context;
};

// Hook to get warehouse ID for API calls
export const useWarehouseId = (): number | null => {
  const { selectedWarehouse } = useWarehouse();
  const warehouseId = selectedWarehouse?.id || null;
  return warehouseId;
};

// Hook to check if a specific warehouse is selected
export const useIsWarehouseSelected = (warehouseId?: number): boolean => {
  const { selectedWarehouse } = useWarehouse();
  if (warehouseId) {
    return selectedWarehouse?.id === warehouseId;
    }
  return selectedWarehouse !== null;
};